import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs'
import { AssessmentDto } from 'src/app/models/flip/assessment-dto'
import { DeleteAssessmentRequestDto } from 'src/app/models/flip/delete-assessment-request'
import { EditAssessmentDateRequestDto } from 'src/app/models/flip/edit-assessment-date-request'
import { FlipHistoryDto } from 'src/app/models/flip/flip-history-dto'
import { ApplicationInsightsService } from '../application-insights/application-insights.service'
import { ConfigService } from '../config/config.service'
import { FlipPopulationData } from '../../models/flip/flip-population-data'

@Injectable({
  providedIn: 'root',
})
export class FlipService {
  private readonly flipInstanceUrl =
    this.config.getSettings('webApi').baseUri + 'flip/'
  private readonly flipInstanceItemsUrl =
    this.flipInstanceUrl + 'getassessmentbyid'
  private readonly assessmentItemsUrl = this.flipInstanceUrl + 'getassessment'
  private readonly submitUrl = this.flipInstanceUrl + 'submit'
  private readonly getLastAssessmentUrl =
    this.flipInstanceUrl + 'getlastassessment'
  private notesValueSource = new BehaviorSubject<string>('')
  private readonly flipHistoryUrl = this.flipInstanceUrl + 'history'
  private readonly editAssessmentDateUrl = this.flipInstanceUrl + 'edit-assessment-date'
  private readonly deleteAssessmentUrl = this.flipInstanceUrl + 'delete-assessment'

  constructor(
    private http: HttpClient,
    private appInsights: ApplicationInsightsService,
    private config: ConfigService,
  ) {}

  SetNoteValue(value: string): void {
    this.notesValueSource.next(value)
  }

  GetLastAssessment(clientId: string): Observable<AssessmentDto> {
    const params: Record<string, string> = { clientId: clientId }
    return this.http
      .get<AssessmentDto>(this.getLastAssessmentUrl, { params: params })
      .pipe(
        tap((_) =>
          this.appInsights.logEvent(
            'Retrieved FLI-P Assessment from Dataverse.',
          ),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  GetAssessmentInstanceById(instanceId: string): Observable<AssessmentDto> {
    const params: Record<string, string> = { flipInstanceId: instanceId }
    return this.http
      .get<AssessmentDto>(this.flipInstanceItemsUrl, { params: params })
      .pipe(
        tap((_) =>
          this.appInsights.logEvent(
            'Retrieved flip Instance Detail from DataVerse.',
          ),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  GetAssessment(clientId: string): Observable<AssessmentDto> {
    const params: Record<string, string> = { clientId: clientId }
    return this.http.get<AssessmentDto>(this.assessmentItemsUrl, { params: params }).pipe(
      tap((_) =>
        this.appInsights.logEvent(
          'Retrieved assessment details from DataVerse.',
        ),
      ),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  Submit(assessmentDto: AssessmentDto) {
    return this.http.post<any>(this.submitUrl, assessmentDto).pipe(
      tap((_) => this.appInsights.logEvent('Assessment saved.')),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  History(clientId: string): Observable<FlipHistoryDto[]> {
    const params: Record<string, string> = { clientId: clientId }
    return this.http
      .get<FlipHistoryDto[]>(this.flipHistoryUrl, { params: params })
      .pipe(
        tap((_) =>
          this.appInsights.logEvent('Retrieved FLI-P History from Dataverse'),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  EditAssessmentDate(request: EditAssessmentDateRequestDto): Observable<void> {
    return this.http.post<void>(this.editAssessmentDateUrl, request).pipe(
      tap((_) => this.appInsights.logEvent('FLI-P assessment date edited.')),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  DeleteAssessment(request: DeleteAssessmentRequestDto): Observable<void> {
    return this.http.post<void>(this.deleteAssessmentUrl, request).pipe(
      tap((_) => this.appInsights.logEvent('FLI-P assessment deleted.')),
      catchError(async (err) => {
        throw err
      }),
    )
  }

  Population(): Observable<FlipPopulationData> {
    return this.http.get<FlipPopulationData>(this.flipInstanceUrl + 'getflippopulation').pipe(
      tap((_) =>
        this.appInsights.logEvent('Retrieved FLI-P Population from Dataverse'),
      ),
      catchError(async (err) => {
        throw err
      }),
    )
  }
}
